import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DataRequestsState } from '@/types/dataRequestsState';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { PaginationResponse } from '@/types/pagination';
import { GetTransactionResponse } from '@/domains/transactions/types/responses/GetTransactionResponse';
import { TableSortingConfiguration } from '@/features/table/types/TableSortingConfiguration';
import { GetPaymentDetailsPayload } from '../types/GetPayeeDetailsPayload';
import { AdjustmentEntities } from '@/domains/adjustments/enums/AdjustmentEntities';
import { SortDirection } from '@/enums/SortDirection';

type PaymentsSliceState = {
  paymentsMapByClaimId: {
    [claimId: number]: {
      paymentList: NormalizedV2Response[];
      isLoading: boolean;
    };
  };
  paymentDetails: NormalizedV2Response | null;
  transactionDetails: {
    byPaymentId: {
      [paymentId: string]: {
        listings: NormalizedV2Response[];
        pagination: PaginationResponse;
        sorting: TableSortingConfiguration;
        hideCompleteTransactions: boolean;
      };
    };
  };
  statuses: {
    isPaymentDetailsLoading: boolean;
    paymentTransactionsDetails: {
      [paymentId: string]: DataRequestsState;
    };
  };
};

const initialState: PaymentsSliceState = {
  paymentsMapByClaimId: {},
  paymentDetails: null,
  transactionDetails: {
    byPaymentId: {},
  },
  statuses: {
    isPaymentDetailsLoading: false,
    paymentTransactionsDetails: {},
  },
};

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    getPayments(state, action: PayloadAction<{ claimId: number }>) {
      state.paymentsMapByClaimId[action.payload.claimId] = {
        paymentList: [],
        isLoading: true,
      };
    },
    getPaymentsSuccess(state, action: PayloadAction<{ dataList: NormalizedV2Response[]; claimId: number }>) {
      state.paymentsMapByClaimId[action.payload.claimId] = {
        paymentList: action.payload.dataList,
        isLoading: false,
      };
    },
    getPaymentDetails: (state, _action: PayloadAction<GetPaymentDetailsPayload>) => {
      state.statuses.isPaymentDetailsLoading = true;
    },
    getPaymentDetailsSuccess: (state, { payload }: PayloadAction<any>) => {
      state.paymentDetails = payload;
      state.statuses.isPaymentDetailsLoading = false;
    },
    getPaymentDetailsError: (state) => {
      state.statuses.isPaymentDetailsLoading = false;
    },
    deletePayment: (_, _action: PayloadAction<{ claimId: number | string; paymentId: number | string }>) => {},
    deletePaymentPayee: (
      _,
      _action: PayloadAction<{
        claimId: number | string;
        payeeId: number | string;
        entityTypeToRefresh: AdjustmentEntities;
      }>,
    ) => {},
    deletePaymentAdjustment: (
      _,
      _action: PayloadAction<{ claimId: string | number; adjustmentId: string | number }>,
    ) => {},
    deletePaymentSplit: (_, _action: PayloadAction<{ splitId: number; claimId: number }>) => {},
    createPaymentTransaction(_, _action: PayloadAction<{ claimId: number; paymentId: number }>) {},
    getPaymentTransactionsDetails: (state, { payload }: PayloadAction<{ paymentId: string | number }>) => {
      const { paymentId } = payload;

      if (!state.transactionDetails.byPaymentId[paymentId]) {
        state.transactionDetails.byPaymentId[paymentId] = {
          listings: [],
          pagination: {
            numberOfItems: 0,
            first: '1',
            last: undefined,
            previous: '0',
            next: '0',
            page: 1,
            perPage: 5,
          },
          sorting: {
            sortBy: 'createdAt',
            sortDirection: SortDirection.ASC,
          },
          hideCompleteTransactions: false,
        };
      }
    },
    startLoadingPaymentTransactionsDetails: (state, { payload }: PayloadAction<{ paymentId: string | number }>) => {
      state.statuses.paymentTransactionsDetails[payload.paymentId] = {
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },
    getPaymentTransactionDetailsSuccess: (
      state,
      {
        payload: { paymentId, data, shouldRewrite = true },
      }: PayloadAction<{
        paymentId: string | number;
        data: GetTransactionResponse;
        shouldRewrite?: boolean;
      }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];

      const payment = state.transactionDetails.byPaymentId[paymentId];

      if (shouldRewrite || !payment) {
        state.transactionDetails.byPaymentId[paymentId] = {
          listings: listingsArray,
          pagination: data.pagination,
          sorting: data.sorting,
          hideCompleteTransactions: data.hideCompleteTransactions,
        };
      } else {
        state.transactionDetails.byPaymentId[paymentId] = {
          listings: [...payment.listings, ...listingsArray],
          pagination: data.pagination,
          sorting: data.sorting,
          hideCompleteTransactions: data.hideCompleteTransactions,
        };
      }

      state.statuses.paymentTransactionsDetails[paymentId] = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getPaymentTransactionDetailsError: (state, { payload }: PayloadAction<{ paymentId: string | number }>) => {
      state.statuses.paymentTransactionsDetails[payload.paymentId] = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    incrementPaymentTransactionsPage: (state, { payload }: PayloadAction<{ paymentId: string | number }>) => {
      const current = state.transactionDetails.byPaymentId[payload.paymentId];
      if (current) {
        current.pagination.page++;
      }
    },
    decrementPaymentTransactionsPage: (state, { payload }: PayloadAction<{ paymentId: string | number }>) => {
      const current = state.transactionDetails.byPaymentId[payload.paymentId];
      if (current && current.pagination.page > 1) {
        current.pagination.page--;
      }
    },
    setTransactionDetailsSorting: (
      state,
      { payload }: PayloadAction<{ paymentId: string | number; sorting: TableSortingConfiguration }>,
    ) => {
      const { paymentId, sorting } = payload;
      if (state.transactionDetails.byPaymentId[paymentId]) {
        state.transactionDetails.byPaymentId[paymentId].sorting = sorting;
        state.transactionDetails.byPaymentId[paymentId].pagination.page = 1;
      }
    },
    setHideCompleteTransactions: (
      state,
      { payload }: PayloadAction<{ paymentId: string | number; value: boolean }>,
    ) => {
      if (state.transactionDetails.byPaymentId[payload.paymentId]) {
        state.transactionDetails.byPaymentId[payload.paymentId].hideCompleteTransactions = payload.value;
        state.transactionDetails.byPaymentId[payload.paymentId].pagination.page = 1;
      }
    },
    duplicatePaymentTransaction(
      _,
      _action: PayloadAction<{ claimId: string | number; transactionId: string | number; paymentId: string | number }>,
    ) {},
  },
});

export const {
  getPayments,
  getPaymentsSuccess,
  getPaymentDetails,
  getPaymentDetailsError,
  getPaymentDetailsSuccess,
  deletePayment,
  createPaymentTransaction,
  duplicatePaymentTransaction,
  getPaymentTransactionsDetails,
  getPaymentTransactionDetailsSuccess,
  getPaymentTransactionDetailsError,
  startLoadingPaymentTransactionsDetails,
  incrementPaymentTransactionsPage,
  decrementPaymentTransactionsPage,
  setTransactionDetailsSorting,
  setHideCompleteTransactions,
  deletePaymentPayee,
  deletePaymentSplit,
} = paymentsSlice.actions;

export const paymentsReducer = paymentsSlice.reducer;
export const paymentsReducerPath = paymentsSlice.reducerPath;
